import { Component, Inject, OnInit } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { Item } from '@app/core/components/listbox/listbox.component';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { StateService } from '@app/core/services/state.service';
import { TranslateService } from '@app/core/services/translate.service';
import { CaseGet, CaseService } from '@app/services/case.service';
import { LogisticService } from '@app/services/logistic.service';

@Component({
    selector: 'tt-case',
    templateUrl: './case.component.html',
    styleUrls: ['./case.component.css'],
    standalone: false,
})
export class CaseComponent implements OnInit {
    public page?: CaseGet;
    public grid?: GridOptions;
    public lockedSave = false;
    public selectListReports: Item[] = [];
    public selectListPrinterGroups: Item[] = [];
    public selectListPrinterLocations: Item[] = [];
    public selectListPrinters: Item[] = [];

    private translations = { case_show_title: 'Varsel', case_show_ok_label: 'OK' };

    constructor(
        private caseService: CaseService,
        private logistic: LogisticService,
        private rememberService: RememberService,
        private state: StateService,
        private modal: ModalService,
        private translate: TranslateService,
        @Inject('utilityService') private utility: LegacyUtilityService,
    ) {}

    public get showPrinterGroupOptions(): boolean {
        return Number(this.page?.printergroup_id ?? 0) > 0;
    }

    public get showPrinterLocation(): boolean {
        return Number(this.page?.printergroup_id ?? 0) > 1;
    }

    public async onPrinterGroupChanged(value: string): Promise<void> {
        if (!this.page) return;
        this.page.printergroup_id = value;

        if (Number(value) > 0) await this.loadPrinters();
        if (this.utility.validateParmsValue(value)) this.rememberValue('printergroup_id', value);
    }

    public async onPrinterLocationChanged(value: string): Promise<void> {
        if (!this.page) return;
        this.page.printerlocation_id = value;
        await this.loadPrinters();
        if (this.utility.validateParmsValue(value)) this.rememberValue('printerlocation_id', value);
    }

    public onPrinterChanged(value: string): void {
        if (!this.page) return;
        this.page.printer_id = value;
        if (this.utility.validateParmsValue(value)) this.rememberValue('printer_id', value);
    }

    public onReportChanged(value: string): void {
        if (!this.page) return;
        this.page.report_id = value;
        if (this.utility.validateParmsValue(value)) this.rememberValue('report_id', value);
    }

    public async saveChanges(): Promise<void> {
        if (!this.page) return;
        this.lockedSave = true;
        try {
            const saveResponse = (await this.caseService.saveCase(this.page))[0];
            if (saveResponse?.errorcode !== '0') {
                await this.modal.show({
                    type: 'warning',
                    title: this.translations.case_show_title,
                    message: saveResponse?.errormessage ?? '',
                    buttons: [{ label: this.translations.case_show_ok_label, cssClass: 'btn-warning', type: 'warning', close: true }],
                });
                return;
            }
            this.state.back();
        } finally {
            this.lockedSave = false;
        }
    }

    private async initialize(): Promise<void> {
        const caseKeyno = this.state.getStateParams()['case_keyno'] ?? '';
        if (!caseKeyno) return;

        const caseList = await this.caseService.getCase({ case_keyno: caseKeyno });
        this.page = caseList[0];
        if (!this.page) return;

        this.grid = new Grid()
            .setRememberId('w_case_stockbatches_grid')
            .setLoadDataFunction({ method: 2559, parameters: this.page })
            .setSpecialFunc({ newTab: true })
            .setCSSOptions({ altColor: true, textWrapping: false })
            .setKendoConfig({ height: null, aggregate: true, pager: false, selectable: false, filterable: false });

        const translation = await this.translate.translateBatch(['case_show_title', 'case_show_ok_label']);
        this.translations.case_show_title = translation['case_show_title'] ?? this.translations.case_show_title;
        this.translations.case_show_ok_label = translation['case_show_ok_label'] ?? this.translations.case_show_ok_label;

        [this.selectListPrinterGroups, this.selectListPrinterLocations, this.selectListReports] = await Promise.all([
            this.logistic.getPrinterGroups(),
            this.logistic.getPrinterLocations(),
            this.logistic.getReports('case'),
        ]);
        await this.loadPrinters();
    }

    private async loadPrinters(): Promise<void> {
        if (!this.page) return;
        const printerGroupId = Number.parseInt(this.page.printergroup_id, 10);
        const printerLocationId = Number.parseInt(this.page.printerlocation_id, 10);
        this.selectListPrinters = await this.logistic.getAllPrinters(printerGroupId, printerLocationId);
    }

    private rememberValue(id: CasePrinterPreference, value: string): void {
        const key = `w_${this.state.getCurrentName()}.${id}`;
        if (this.utility.validateParmsValue(key)) void this.rememberService.remember(key, value);
    }

    public ngOnInit(): void {
        void this.initialize();
    }
}

type CasePrinterPreference = 'printergroup_id' | 'printerlocation_id' | 'printer_id' | 'report_id';

interface LegacyUtilityService {
    validateParmsValue(value: unknown): boolean;
}
